import React, { useState } from "react";
import ProductPageLayout from "@/components/ProductPageLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { 
  Calendar, 
  Users, 
  Gift, 
  Star,
  CheckCircle,
  ArrowRight,
  TrendingUp,
  Heart,
  PartyPopper,
  Cake,
  Sparkles,
  UserPlus,
  Clock,
  Zap
} from "lucide-react";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import TrustLogosSection from "@/components/trust/TrustLogosSection";
import HeroBadgesSection from "@/components/hero/HeroBadgesSection";
import milestonesHeroImage from "@/assets/milestones-hero.png";
import { MilestoneTypesSection } from "@/components/milestones/MilestoneTypesSection";

const Milestones = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  const benefits = [
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: "85% Higher Participation",
      description: "Teams see increased engagement when milestones are celebrated automatically"
    },
    {
      icon: <Heart className="h-6 w-6" />,
      title: "92% Feel More Valued",
      description: "Employees report feeling significantly more appreciated with milestone recognition"
    },
    {
      icon: <PartyPopper className="h-6 w-6" />,
      title: "73% Retention Improvement",
      description: "Companies see better retention rates with consistent milestone celebrations"
    }
  ];

  return (
    <ProductPageLayout
      title="Employee Milestone Recognition Platform - Automated Celebrations"
      description="Automate birthday, anniversary, and new hire celebrations with Assembly. Integrate with HRIS systems and send to Slack or MS Teams instantly."
      keywords="work anniversary software, employee milestones, birthday recognition, new hire onboarding, anniversary bot, milestone celebrations, automated recognition"
      ctaTitle="Ready to Automate Milestone Celebrations?"
      ctaDescription="Join 4,000+ companies making every employee milestone memorable with automated celebrations."
      ctaButtonText="Book My Demo"
      ctaVariant="orange"
      onCtaClick={() => setIsDemoModalOpen(true)}
    >
      {/* Full Height Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-50 via-gray-50 to-orange-50/30 font-sans overflow-hidden min-h-screen">
        {/* Subtle Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-orange-500/8 via-amber-500/5 to-orange-400/4 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] bg-gradient-to-br from-amber-400/6 via-orange-500/5 to-slate-400/3 rounded-full blur-3xl animate-pulse" style={{
          animationDelay: '1s'
        }}></div>
        </div>
        
        {/* Dot Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.15]">
          <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(251, 146, 60, 0.3) 2px, transparent 0)`,
          backgroundSize: '48px 48px'
        }}></div>
        </div>

        <section className="relative flex items-center justify-center py-8 lg:py-10 px-4 lg:px-8 min-h-screen pt-20 lg:pt-24">
          <div className="container mx-auto px-4 2xl:px-12 max-w-none lg:max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1600px] relative px-6 lg:px-6 xl:px-8 pb-3 lg:pb-4">
            <div className="flex flex-col lg:grid lg:grid-cols-7 gap-6 lg:gap-4 items-start">
              {/* Left Section: Text Content */}
              <div className="flex flex-col items-start text-left lg:col-span-4 h-full justify-center space-y-4 lg:space-y-6">
                {/* Pills */}
                <motion.div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3 mt-6 lg:mt-8 mb-3 lg:mb-4" initial={{
                opacity: 0,
                y: -20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.7
              }}>
                  <div className="relative overflow-hidden px-3 py-1.5 rounded-full bg-black/5 border border-black/10 backdrop-blur-sm hover:bg-black/10 transition-all duration-300 hover:-translate-y-0.5">
                    <h1 className="text-xs font-normal text-gray-900 tracking-wide uppercase">Never Miss a Milestone</h1>
                  </div>
                </motion.div>

                {/* Main Heading */}
                <div className="mb-4 lg:mb-6">
                  <motion.h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-5xl xl:text-6xl font-bold text-black tracking-tight font-sans" initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.8,
                  delay: 0.2
                }}>
                    Celebrate every milestone automatically
                  </motion.h2>
                </div>

                {/* Subheading */}
                <motion.p className="text-sm sm:text-base lg:text-base xl:text-lg text-gray-600 mb-6 lg:mb-8 leading-relaxed" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.8,
                delay: 0.4
              }}>
                  Automatically celebrate birthdays, work anniversaries, and new hires with Assembly. 
                  Pull data from your HRIS and send celebrations to Slack or MS Teams - no manual work required.
                </motion.p>
                
                {/* Action Buttons */}
                <motion.div className="mb-4 lg:mb-6 w-full flex flex-col sm:flex-row gap-3" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.8,
                delay: 0.6
              }}>
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md transition-all duration-200" 
                    onClick={() => setIsDemoModalOpen(true)}
                  >
                    Book a Demo
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="w-full sm:w-auto bg-white border-2 border-gray-300 text-gray-900 hover:bg-gray-100 hover:text-gray-900 hover:border-gray-400 shadow-sm transition-all duration-200" 
                    asChild
                  >
                    <a href="https://app.joinassembly.com/signup" target="_blank" rel="noopener noreferrer">
                      Start for Free
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </motion.div>
                
                {/* Trust Ratings */}
                <motion.div className="flex flex-col sm:flex-row gap-3 sm:gap-6 items-start sm:items-center w-full" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.8,
                delay: 0.8
              }}>
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400 fill-yellow-400" />
                    <span className="text-sm font-semibold text-gray-700">4.9 / 5 on G2</span>
                    <img src="/lovable-uploads/e68d106f-69a9-45e8-8624-7f09b1ae1e6f.png" alt="G2 Logo" className="h-4 w-4 sm:h-5 sm:w-5" width={20} height={20} />
                  </div>
                  <a href="https://www.g2.com/products/assembly-2021-12-09/reviews" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                    <span className="text-sm font-semibold text-gray-500">3000+ reviews</span>
                  </a>
                </motion.div>
              </div>
              
              {/* Right Section: Hero Image */}
              <div className="relative w-full h-full flex items-center justify-center lg:col-span-3 mt-6 lg:mt-0">
                <div className="relative group">
                  <motion.img 
                    src={milestonesHeroImage} 
                    alt="Automated milestone celebrations showing birthday, anniversary, and welcome messages" 
                    className="w-full h-auto object-cover rounded-xl transition-transform duration-300 group-hover:scale-105" 
                    loading="eager" 
                    decoding="async" 
                    fetchPriority="high" 
                    initial={{
                    opacity: 0,
                    scale: 0.95
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} transition={{
                    duration: 0.8,
                    delay: 0.4
                  }} />
                </div>
              </div>
            </div>
            
            {/* Trust Section at Bottom */}
            <motion.div className="text-center pt-8 lg:pt-12 mt-8 space-y-8" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 1.0
          }}>
              <p className="text-lg text-gray-600">
                The #1 Employee Recognition & Rewards Platform - Trusted by Leading Teams Worldwide
              </p>
              <HeroBadgesSection />
              <div className="pt-4">
                <TrustLogosSection />
              </div>
            </motion.div>
          </div>
        </section>
      </div>

      <MilestoneTypesSection />

      {/* Work Anniversaries Detail */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }}>
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Cake className="h-4 w-4" />
                Work Anniversaries
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Celebrate every year of service automatically
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Pull anniversary dates from your HRIS and automatically post celebrations at 9 AM company time. 
                Scale rewards year-over-year and make major milestones extra special with enhanced recognition.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">HRIS Integration</h3>
                    <p className="text-gray-600">Sync automatically with Workday, BambooHR, ADP, and more</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Year-over-Year Scaling</h3>
                    <p className="text-gray-600">Increase points each year to reward long-term loyalty</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Major Milestone Recognition</h3>
                    <p className="text-gray-600">Special celebrations for 5, 10, 15+ year anniversaries</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 max-w-md mx-auto">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Cake className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Sarah's 5 Year Anniversary! 🎉</h3>
                  <p className="text-gray-600">Engineering Team</p>
                </div>
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 mb-4">
                  <p className="text-gray-800 text-center">
                    "Congratulations Sarah on 5 amazing years with Assembly! Here's to many more years of innovation and growth together. 🏆 500 points"
                  </p>
                </div>
                <div className="flex justify-center space-x-2">
                  {['🎉', '👏', '❤️', '🚀'].map((emoji, index) => (
                    <div key={index} className={`w-8 h-8 ${index === 0 ? 'bg-orange-100' : index === 1 ? 'bg-blue-100' : index === 2 ? 'bg-pink-100' : 'bg-purple-100'} rounded-full flex items-center justify-center`}>
                      <span className="text-xs">{emoji}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Birthdays Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="order-2 lg:order-1">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 max-w-md mx-auto">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Gift className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Happy Birthday Marcus! 🎂</h3>
                  <p className="text-gray-600">Sales Team</p>
                </div>
                <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg p-4 mb-4">
                  <p className="text-gray-800 text-center">
                    "Wishing you an amazing birthday Marcus! Thank you for bringing such positive energy to our team every day. 🎈"
                  </p>
                </div>
                <div className="flex justify-center space-x-2">
                  {['🎂', '🎉', '🎁', '🥳'].map((emoji, index) => (
                    <div key={index} className={`w-8 h-8 ${index === 0 ? 'bg-pink-100' : index === 1 ? 'bg-rose-100' : index === 2 ? 'bg-purple-100' : 'bg-orange-100'} rounded-full flex items-center justify-center`}>
                      <span className="text-xs">{emoji}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-pink-100 text-pink-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Gift className="h-4 w-4" />
                Birthdays
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Make every birthday feel special
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Automatically celebrate birthdays with personalized messages sent to Slack or MS Teams. 
                Pull birthday data from your HRIS and never miss an opportunity to show you care.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                  <span className="text-gray-700">Automated birthday posts at 9 AM</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                  <span className="text-gray-700">Team-wide visibility in Slack or MS Teams</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                  <span className="text-gray-700">Customizable messages and optional points</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Welcome to Team Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }}>
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <UserPlus className="h-4 w-4" />
                New Hire Welcome
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Start new hires off right from day one
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Create memorable first impressions with automated welcome celebrations. 
                Introduce new hires to the team and make them feel valued from their very first day.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Day-One Celebrations</h3>
                    <p className="text-gray-600">Automatically welcome new hires on their start date</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Team Introductions</h3>
                    <p className="text-gray-600">Share their background and role with the company</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-blue-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Onboarding Rewards</h3>
                    <p className="text-gray-600">Welcome points to start their Assembly journey</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 max-w-md mx-auto">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <UserPlus className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Welcome Alex to the Team! 👋</h3>
                  <p className="text-gray-600">Product Design</p>
                </div>
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 mb-4">
                  <p className="text-gray-800 text-center">
                    "Let's give a warm welcome to Alex joining us as Senior Product Designer! We're excited to have you on board. 🎊 250 points"
                  </p>
                </div>
                <div className="flex justify-center space-x-2">
                  {['👋', '🎉', '💙', '🚀'].map((emoji, index) => (
                    <div key={index} className={`w-8 h-8 ${index === 0 ? 'bg-blue-100' : index === 1 ? 'bg-cyan-100' : index === 2 ? 'bg-indigo-100' : 'bg-teal-100'} rounded-full flex items-center justify-center`}>
                      <span className="text-xs">{emoji}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HRIS Autopilot */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }}>
              <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Zap className="h-4 w-4" />
                HRIS Autopilot
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Configure once, celebrate forever
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Connect your HRIS and Assembly automatically pulls employee data - birthdays, anniversaries, start dates. 
                Customize your messages, add your company logo, set your preferences, and watch it work on autopilot.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-purple-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Automatic Data Sync</h3>
                    <p className="text-gray-600">Pull employee data from Workday, BambooHR, ADP, and more - no manual entry</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-purple-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Personalize Everything</h3>
                    <p className="text-gray-600">Custom messages, company logo, GIFs, point values - make it your own</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-purple-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Set It and Forget It</h3>
                    <p className="text-gray-600">Configure once and watch celebrations happen automatically - forever</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 max-w-md mx-auto">
                <div className="mb-6">
                  <h4 className="font-bold text-gray-900 mb-4">Configuration Dashboard</h4>
                  <div className="space-y-3">
                    <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-900">HRIS Connection</span>
                        <span className="text-xs text-green-600 font-medium">✓ Connected</span>
                      </div>
                      <p className="text-xs text-gray-600">BambooHR • Syncing daily at 12 AM</p>
                    </div>
                    
                    <div className="p-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-900">Message Templates</span>
                        <span className="text-xs text-blue-600 font-medium">3 Active</span>
                      </div>
                      <p className="text-xs text-gray-600">Birthday • Anniversary • Welcome</p>
                    </div>
                    
                    <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-900">Personalization</span>
                        <span className="text-xs text-blue-600 font-medium">Configured</span>
                      </div>
                      <p className="text-xs text-gray-600">Custom logo • GIFs • Point values</p>
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Autopilot Status</p>
                      <p className="text-xs text-gray-600">12 celebrations scheduled</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm text-green-600 font-semibold">Active</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Slack & MS Teams Delivery */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-indigo-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="order-2 lg:order-1">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 max-w-md mx-auto">
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                        <Users className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">Slack</h4>
                        <p className="text-xs text-gray-600">#celebrations channel</p>
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded border border-purple-100">
                      <p className="text-xs text-gray-700 mb-2">🎉 Happy Birthday Marcus!</p>
                      <p className="text-xs text-gray-500">Posted at 9:00 AM • 24 reactions</p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                        <Users className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">Microsoft Teams</h4>
                        <p className="text-xs text-gray-600">Company Wide team</p>
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded border border-blue-100">
                      <p className="text-xs text-gray-700 mb-2">🎊 Sarah's 5 Year Anniversary!</p>
                      <p className="text-xs text-gray-500">Posted at 9:00 AM • 18 likes</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Delivery Status</span>
                    <span className="text-green-600 font-semibold flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" />
                      All Delivered
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Users className="h-4 w-4" />
                Deliver Where They Work
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Celebrations delivered to Slack & MS Teams
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Send milestone celebrations directly to your team's communication platform. 
                Whether you use Slack or Microsoft Teams, celebrations appear automatically where your team is already engaged.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-700">Post to specific channels or company-wide</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-700">Automatic posting at 9 AM company time</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-700">Rich formatting with emojis and reactions</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              The impact of automated milestones
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Companies that celebrate milestones consistently see measurable improvements
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center hover:shadow-xl transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-6 text-white">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <DemoBookingModal 
        open={isDemoModalOpen}
        onOpenChange={setIsDemoModalOpen}
      />
    </ProductPageLayout>
  );
};

export default Milestones;
